import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";

export default function Account() {
  const { user, subscription, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [payments, setPayments] = useState([]);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get("/account/payments").then(({ data }) => setPayments(data)).catch(() => {});
  }, []);

  const saveProfile = async () => {
    try { await API.put("/account/profile", { name }); await refresh(); toast.success("Profile updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const changePassword = async () => {
    try {
      await API.post("/account/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "" });
      toast.success("Password updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cancelSub = async () => {
    setBusy(true);
    try {
      await API.post("/payments/cancel");
      await refresh();
      toast.success("Subscription cancelled — you keep access until your paid period ends");
      setCancelOpen(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await API.delete("/account", { data: { password: deletePw } });
      await logout();
      navigate("/");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const resetProgress = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/account/reset-progress");
      toast.success(`Progress reset — ${data.deleted_attempts} answers cleared. Every question is unanswered again.`);
      setResetOpen(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const sub = subscription || {};
  const planNames = { pamind_monthly: "Monthly", pamind_6month: "6 Months", pamind_annual: "12 Months" };

  return (
    <div data-testid="account-page" className="max-w-3xl space-y-8">
      <PageHeader title="Account settings" sub={user?.email} />

      <section className="bg-card border border-border rounded-lg p-6" data-testid="profile-section">
        <h3 className="font-display font-semibold mb-4">Profile</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2 flex-1 min-w-[200px]">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button data-testid="profile-save-btn" onClick={saveProfile}>Save</Button>
        </div>
      </section>

      <section className="bg-card border border-border rounded-lg p-6" data-testid="subscription-section">
        <h3 className="font-display font-semibold mb-4">Subscription</h3>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm" data-testid="subscription-status">
              <span className="font-semibold">{planNames[sub.plan] || "No active plan"}</span>
              {sub.status && sub.status !== "none" && (
                <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${sub.active ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                  {sub.status === "cancelled" ? "Cancelled — active until expiry" : sub.status}
                </span>
              )}
            </p>
            {sub.end_date && <p className="text-xs text-muted-foreground mt-1">Access until {fmtDate(sub.end_date)}{sub.auto_renew && sub.status === "active" ? " (renews automatically)" : ""}</p>}
          </div>
          <div className="flex gap-2">
            {sub.auto_renew && sub.status === "active" && (
              <Button variant="outline" data-testid="cancel-subscription-btn" onClick={() => setCancelOpen(true)}>Cancel subscription</Button>
            )}
            {!sub.active && <Button data-testid="renew-subscription-btn" onClick={() => navigate("/pricing")}>Choose a plan</Button>}
          </div>
        </div>
      </section>

      <section className="bg-card border border-border rounded-lg p-6" data-testid="payments-section">
        <h3 className="font-display font-semibold mb-4">Payment history</h3>
        {payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No payments yet.</p>
        ) : (
          <div className="divide-y divide-border" data-testid="payments-list">
            {payments.map((p) => (
              <div key={p.session_id} className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="font-medium">{planNames[p.lookup_key] || p.lookup_key}</span>
                <span className="text-muted-foreground">{fmtDate(p.created_at)}</span>
                <span className="font-semibold">£{p.amount?.toFixed(2)}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.payment_status === "paid" ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                  {p.payment_status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-card border border-border rounded-lg p-6" data-testid="password-section">
        <h3 className="font-display font-semibold mb-4">Change password</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="cur-pw">Current password</Label>
            <Input id="cur-pw" data-testid="current-password-input" type="password" value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pw">New password</Label>
            <Input id="new-pw" data-testid="new-password-input" type="password" value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })} />
          </div>
        </div>
        <Button data-testid="change-password-btn" onClick={changePassword} className="mt-4" disabled={!pw.current || pw.next.length < 8}>
          Update password
        </Button>
      </section>

      <section className="bg-card border border-border rounded-lg p-6" data-testid="reset-progress-section">
        <h3 className="font-display font-semibold mb-2">Reset question progress</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Finished the bank? Clears your entire answer history and recent sessions so every question becomes unanswered again — perfect for a fresh run. Your flagged questions and subscription are kept.
        </p>
        <Button variant="outline" data-testid="reset-progress-btn" onClick={() => setResetOpen(true)}>Reset my progress</Button>
      </section>

      <section className="bg-card border border-destructive/30 rounded-lg p-6" data-testid="danger-section">
        <h3 className="font-display font-semibold text-destructive mb-2">Delete account</h3>
        <p className="text-sm text-muted-foreground mb-4">Permanently deletes your account, subscription record and all question history. This cannot be undone.</p>
        <Button variant="destructive" data-testid="delete-account-btn" onClick={() => setDeleteOpen(true)}>Delete my account</Button>
      </section>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel subscription?</DialogTitle>
            <DialogDescription>Your monthly plan will not renew. You keep full access until {fmtDate(sub.end_date)}. Your question history is kept permanently.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="destructive" data-testid="confirm-cancel-btn" onClick={cancelSub} disabled={busy}>Yes, cancel</Button>
            <Button variant="outline" data-testid="keep-plan-btn" onClick={() => setCancelOpen(false)}>Keep my plan</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-progress-dialog">
          <DialogHeader>
            <DialogTitle>Reset all question progress?</DialogTitle>
            <DialogDescription>
              This permanently deletes your answer history and session history: every question will count as unanswered, your recent sessions list will be cleared, and all progress bars, accuracy stats and streaks return to zero. Flagged questions are kept. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="destructive" data-testid="confirm-reset-progress-btn" onClick={resetProgress} disabled={busy}>
              {busy ? "Resetting…" : "Yes, reset everything"}
            </Button>
            <Button variant="outline" data-testid="keep-progress-btn" onClick={() => setResetOpen(false)}>Keep my progress</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>This permanently removes your account and all progress. Enter your password to confirm.</DialogDescription>
          </DialogHeader>
          <Input data-testid="delete-password-input" type="password" placeholder="Your password"
            value={deletePw} onChange={(e) => setDeletePw(e.target.value)} />
          <div className="flex gap-3 pt-2">
            <Button variant="destructive" data-testid="confirm-delete-btn" onClick={deleteAccount} disabled={busy || !deletePw}>Delete permanently</Button>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
